import { filterDuplicates, getErrorMessage } from '@/shared/lib'
import { useAppStore, useVODStore } from '@/shared/store'
import { selectSetVodRowItems } from '@/shared/store/storeSelectors/vodSelectors'
import { useLayoutEffect, useRef, useState } from 'react'
import VodItem from '../vodClasses/vodItem'
import { useVODService } from '@/app/context/DIContext/DIServices'
import { fetchItemsAmount } from '@/shared/constants'

export function useVodItems(id: string | number) {
    const setVodRowItems = useVODStore(selectSetVodRowItems)
    const { getVodItems, createId } = useVODService()
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const { actualProfile } = useAppStore()
    const { id: profileId } = actualProfile ?? {}
    const vodItemsToAdd = fetchItemsAmount

    const rowItemsRef = useRef<VodItem[]>([])
    const rowIdRef = useRef('')

    const [, forceUpdate] = useState({})

    const setRowItems = (items: VodItem[]) => {
        forceUpdate({})
        rowItemsRef.current = [...items]
        rowIdRef.current = ''
    }

    useLayoutEffect(() => {
        rowItemsRef.current = []
        rowIdRef.current = ''
        forceUpdate({})
    }, [id])

    const getItems = async (rowId?: string): Promise<VodItem[]> => {
        if (isLoading) return []

        setIsLoading(true)

        const startItemIndex = rowItemsRef.current.length
        const responseId = createId(rowId || id)

        if (
            rowIdRef.current !== responseId &&
            rowItemsRef.current.length === 0
        ) {
            rowItemsRef.current = []
            forceUpdate({})
        }

        try {
            setIsError(false)
            const response = await getVodItems(
                responseId,
                startItemIndex,
                vodItemsToAdd,
                profileId
            )

            if (response.length) {
                // Update store
                setVodRowItems(responseId, response)

                // Merge new items with existing ones
                const newData = [...rowItemsRef.current, ...response]
                rowItemsRef.current = filterDuplicates(newData, 'id')
                rowIdRef.current = responseId

                return response
            }
        } catch (error) {
            setIsError(true)
            getErrorMessage('useVodItems', error)
            return []
        } finally {
            setIsLoading(false)
        }

        return []
    }

    const rowItems = rowItemsRef.current

    return {
        rowItems,
        getItems,
        setRowItems,
        isLoading,
        isError,
    }
}
